import { useOrgStore } from '@/store/org';

const orgStore = useOrgStore();

/*
 * 判断value是否为一个整数
 */
function isInteger(value: number) {
  return Number.isInteger(value);
}
/*
 * 将一个浮点数转成整数，返回整数和倍数。如 3.14 >> 314，倍数是 100
 * @param floatNum {number} 小数
 * @return {object}
 *   {times:100, num: 314}
 */
function toInteger(floatNum: number) {
  // 初始化数字与精度 times精度倍数  num转化后的整数
  const ret = { times: 1, num: 0 };
  const isNegative = floatNum < 0; // 是否是小数
  if (isInteger(floatNum)) { // 是否是整数
    ret.num = floatNum;
    return ret; // 是整数直接返回
  }
  const str = `${floatNum}`; // 转换为字符串
  const dotPos = str.indexOf('.');
  const len = str.substring(dotPos + 1).length; // 拿到小数点之后的位数
  // eslint-disable-next-line no-restricted-properties
  const times = Math.pow(10, len); // 精度倍数
  /*
   * 为什么加0.5?
   *  前面讲过乘法也会出现精度问题
   *  假设传入0.16344556此时倍数为100000000
   *  Math.abs(0.16344556) * 100000000=0.16344556*10000000=1634455.5999999999
   *  少了0.0000000001
   *  加上0.5 0.16344556*10000000+0.5=1634456.0999999999 parseInt之后乘法的精度问题得以矫正
   */
  let intNum = parseInt(`${Math.abs(floatNum) * times + 0.5}`, 10);
  ret.times = times;
  if (isNegative) {
    intNum = -intNum;
  }
  ret.num = intNum;
  return ret;
}

/*
 * 核心方法，实现加减乘除运算，确保不丢失精度
 * 思路：把小数放大为整数（乘），进行算术运算，再缩小为小数（除）
 * @param num1 {number} 运算数1
 * @param num2 {number} 运算数2
 */
function operation(num1: number, num2: number, optType: string) {
  const o1 = toInteger(num1);
  const o2 = toInteger(num2);
  const n1 = o1.num; // 3.25+3.153
  const n2 = o2.num;
  const t1 = o1.times;
  const t2 = o2.times;
  const max = t1 > t2 ? t1 : t2;
  let result = null;
  switch (optType) {
    // 加减需要根据倍数关系来处理
    case 'add':
      if (t1 === t2) { // 两个小数倍数相同
        result = n1 + n2;
      } else if (t1 > t2) {
        // o1 小数位 大于 o2
        result = n1 + n2 * (t1 / t2);
      } else { // o1小数位小于 o2
        result = n1 * (t2 / t1) + n2;
      }
      return result / max;
    case 'subtract':
      if (t1 === t2) {
        result = n1 - n2;
      } else if (t1 > t2) {
        result = n1 - n2 * (t1 / t2);
      } else {
        result = n1 * (t2 / t1) - n2;
      }
      return result / max;
    case 'multiply':
      // 325*3153/(100*1000) 扩大100倍 ==>缩小100倍
      result = (n1 * n2) / (t1 * t2);
      return result;
    case 'divide':
      // (325/3153)*(1000/100) 缩小100倍 ==>扩大100倍
      result = (n1 / n2) * (t2 / t1);
      return result;
    default:
      return 0;
  }
}

// 加减乘除的四个接口
export function add(num1: number, num2: number) {
  return operation(num1, num2, 'add');
}
export function subtract(num1: number, num2: number) {
  return operation(num1, num2, 'subtract');
}
export function multiply(num1: number, num2: number) {
  return operation(num1, num2, 'multiply');
}
export function divide(num1: number, num2: number) {
  return operation(num1, num2, 'divide');
}

/**
 * @description: 根据spaceCode获取蓝海引擎的3d模型数据
 * @param {string} spaceCode 空间编码
 * @return {*} 返回匹配的3D模型数据，未找到则返回null
 */
export const getLanhaiEngine3dModel = (spaceCode: string) => {
  const data = orgStore.lanhaiEngineSpace;
  // 边界检查
  if (!data || data.length === 0 || !spaceCode) {
    return null;
  }

  // 扁平化搜索，提高可读性和性能
  for (const item of data) {
    if (item.code === spaceCode) {
      return item;
    }

    // 使用find方法简化子元素查找
    if (item.children?.length) {
      const found = item.children.find((child:any) => child.code === spaceCode);
      if (found) {
        return found;
      }
    }
  }

  return null;
};
